/**
 * ManageSubscriptionModal — current plan + status + payment method + change
 * plan + cancel/reactivate, gated by ``billingPlatform()``. Ported from the
 * prototype's ``ManageSubscriptionModal`` onto ChromeTokens + PanelSurface.
 *
 * Web: full Stripe-driven controls (payment method, change plan, cancel).
 * iOS/Android: store-managed notice; cancel/change-plan/update-card are hidden
 * per Apple/Google requirements.
 *
 * Includes the Family → Single downgrade warning when the family has more kids
 * than the Single seat allows, an add-a-card mid-trial path (no charge), the
 * ``KeptKidPicker`` (any family member can hold a seat), and the ``DunningBanner``
 * at the top driven by ``billing_state.dunning_stage``. Resolving a failed
 * payment (any dunning stage → successful card update) flips the banner to its
 * one-line resolved success state.
 *
 * Backs launch-ux-followups Item 12 + launch-compliance R8.
 */

import { useContext, useEffect, useMemo, useState } from "react";
import { PanelSurface } from "../chrome/PanelSurface";
import { ChromeTokens } from "../chrome/tokens";
import { Icon } from "../chrome/Icon";
import { DeleteAccountEntry } from "../erasure/DeleteAccountEntry";
import { useFamilyForErasure } from "../erasure/useFamilyForErasure";
import { family as familyApi } from "../api";
import { CheckoutPanel } from "./CheckoutPanel";
import { useBilling } from "./useBilling";
import { billingPlatform, subscriptionRail } from "./platform";
import { appStoreAvailable, manageOnAppStore } from "./appStore";
import { WBM_PLANS, money, planById } from "./plans";
import type { BillingStatus, PlanId } from "./types";
import { KeptKidPicker, type KeptPickerMember } from "./KeptKidPicker";
import { DunningBanner } from "./DunningBanner";
import { defaultSeatHolders, seatHolderLine, seatMembers } from "./seats";
import { AuthContext } from "../hooks/useAuth";
import { useParentalGate } from "../safety/ParentalGate";

interface Props {
  open: boolean;
  onClose: () => void;
  /** The number of active kid profiles — used for the downgrade warning. */
  kidCount?: number;
  /** Open the unlock takeover when a user wants to re-subscribe from locked. */
  onUnlock?: (reason: "locked") => void;
}

const STATUS_BADGE: Record<BillingStatus, { label: string; tokenColor: string }> = {
  trialing: { label: "Free trial", tokenColor: ChromeTokens.color.sun },
  active: { label: "Active", tokenColor: ChromeTokens.color.grass },
  canceled: { label: "Ending soon", tokenColor: ChromeTokens.color.lavender },
  locked: { label: "Paused", tokenColor: ChromeTokens.color.baseMuted },
  free: { label: "Explore mode", tokenColor: ChromeTokens.color.grass },
};

// Decorative tint cycle for picker avatars — kids rotate through these so a
// list of three or more avatars reads as visually distinct without leaning on
// any single accent. The current account holder is always lavender; other
// grown-ups are grass (matches the "On their own plan — not affected" tint).

export function ManageSubscriptionModal({
  open,
  onClose,
  kidCount = 2,
  onUnlock,
}: Props): JSX.Element | null {
  const b = useBilling();
  const family = useFamilyForErasure(open);
  const plat = billingPlatform();
  const rail = subscriptionRail(plat, b.billing_provider);
  const [planTarget, setPlanTarget] = useState<PlanId | null>(null);
  const [addingCard, setAddingCard] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [storeError, setStoreError] = useState(false);
  // Kids Category 1.3: Apple's manage sheet is commerce, so it sits behind the
  // same grown-up check as buying and Restore.
  const gate = useParentalGate();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── kept-creator picker state ─────────────────────────────────────────
  // The tenant id IS the account holder's user id.
  const accountHolderId = useContext(AuthContext)?.tenantId ?? null;
  const [members, setMembers] = useState<KeptPickerMember[] | null>(null);
  const [keptIds, setKeptIds] = useState<string[]>([]);
  const [dunningDismissed, setDunningDismissed] = useState(false);
  // Seat choice pending (a downgrade on Apple's sheet left more members than
  // seats): the grown-up picks here; seeded once the roster is in.
  const [pendingIds, setPendingIds] = useState<string[] | null>(null);
  const [dunningResolved, setDunningResolved] = useState(false);

  const view = b.view;
  const dunningStage = view?.dunningStage ?? null;
  const inDunning = dunningStage === "dunning_first" || dunningStage === "dunning_mid" || dunningStage === "dunning_final";

  // Reset the "card was just updated" success line whenever a fresh dunning
  // stage arrives — the success state belongs to the previous cycle.
  useEffect(() => {
    if (dunningStage) setDunningResolved(false);
  }, [dunningStage]);

  // The plan can change outside the app (Apple's subscription sheet): read it
  // afresh each time the sheet opens rather than trusting the boot-time copy.
  const refreshBilling = b.refresh;
  useEffect(() => {
    if (open) void Promise.resolve().then(refreshBilling).catch(() => {});
  }, [open, refreshBilling]);

  // Load the family roster once the modal opens so the picker is ready the
  // first time the parent selects a smaller plan. Cheap fetch + memoized
  // module-level cache in useFamilyForErasure handles the second hit.
  useEffect(() => {
    if (!open || members !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const roster = await familyApi.getProfiles();
        if (cancelled) return;
        // The account holder first, then kids by when they were added (seats.ts).
        const list = seatMembers(roster, accountHolderId);
        setMembers(list);
      } catch {
        // Offline / unauthorized / no family — leave members null and the
        // downgrade warning falls back to its one-line copy without the picker.
        setMembers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, members, accountHolderId]);

  // Re-seed the kept selection whenever the target plan changes. Self-correct
  // when family size changes underfoot. Only members covered by this plan
  // (i.e. not on their own separate subscription) count toward the seats.
  const pickableMembers = useMemo(
    () => (members ?? []).filter((m) => !m.ownPlan),
    [members],
  );
  useEffect(() => {
    if (planTarget === null) return;
    const seats = planById(planTarget).seats;
    setKeptIds((prev) => defaultSeatHolders(pickableMembers, seats, prev));
  }, [planTarget, pickableMembers]);

  const seatsState = b.view?.seats ?? null;
  const choicePending = !!seatsState?.choicePending;
  useEffect(() => {
    if (!choicePending || !seatsState || members === null) return;
    setPendingIds((prev) => prev ?? defaultSeatHolders(pickableMembers, seatsState.count, seatsState.holderIds));
  }, [choicePending, seatsState, members, pickableMembers]);

  if (!open || !b.isReady || !view) return null;

  const currentPlan = planById(view.planId);
  const badge = STATUS_BADGE[view.status];

  // Source of truth for "this downgrade reduces seats": when we know the
  // roster, use the actual pickable count; otherwise fall back to the
  // ``kidCount`` prop the caller supplied.
  const familySize = pickableMembers.length > 0 ? pickableMembers.length : kidCount;
  const downgradeLosesSeats =
    planTarget !== null
    && planById(planTarget).seats < familySize
    && planById(planTarget).seats < currentPlan.seats;

  const keepCount = planTarget ? planById(planTarget).seats : 0;
  const keptValid = !downgradeLosesSeats || keptIds.length === keepCount;

  const appleBilled = b.billing_provider === "apple";

  const statusLine = (): string => {
    // Apple holds the card and renews it; our price table is not Apple's price.
    if (view.status === "trialing" && appleBilled)
      return `Free trial · ends ${view.endsOn ?? "soon"} · renews through the App Store`;
    if (view.status === "trialing" && view.trialNoCard)
      return `Free trial · ${view.daysLeft} day${view.daysLeft === 1 ? "" : "s"} left · no card yet — add one to keep creating after`;
    if (view.status === "trialing")
      return `Free trial · ${view.daysLeft} day${view.daysLeft === 1 ? "" : "s"} left, then ${money(currentPlan.price)}/mo`;
    // A downgrade on Apple's sheet waits for the renewal; the plan stays as it is until then.
    if (view.status === "active" && view.pendingPlanId && view.pendingPlanId !== view.planId)
      return `${money(currentPlan.price)}/mo · switches to ${planById(view.pendingPlanId).name} on ${view.pendingOn ?? "the next renewal"}`;
    if (view.status === "active") return `${money(currentPlan.price)}/mo · renews monthly`;
    if (view.status === "canceled") return `Ends ${view.endsOn ?? "soon"} · then switches to Explore mode`;
    if (view.status === "free") return "Explore mode · start a free trial to create";
    return "Paused · subscribe to start creating again";
  };

  const run = async (fn: () => Promise<unknown>) => {
    setPending(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPending(false);
    }
  };

  const handleChangePlan = (): void => {
    if (!planTarget || !keptValid) return;
    void run(async () => {
      // Pass the kept ids only on a seat-reducing downgrade — the backend
      // asserts ``len(kept_user_ids) == target.seats`` when present, so a
      // same-plan / upgrade switch omits the param.
      const ids = downgradeLosesSeats ? keptIds : undefined;
      await b.changePlan(planTarget, ids);
      setPlanTarget(null);
    });
  };

  const sectionGap = { display: "flex", flexDirection: "column" as const, gap: ChromeTokens.spacing.md };

  // ── DunningBanner visibility ─────────────────────────────────────────
  // Show the banner whenever the backend reports an active dunning stage or
  // a paused subscription. ``dunning_first`` is the only stage that can be
  // dismissed (other stages are too important to hide). After a successful
  // card update from any dunning state, show the one-line resolved success.
  const showResolved = dunningResolved;
  const showDunning =
    !showResolved
    && dunningStage !== null
    && !(dunningStage === "dunning_first" && dunningDismissed);

  return (
    <PanelSurface open={open} anchor="center" onDismiss={onClose} ariaLabel="Manage subscription">
      {gate.Gate && <gate.Gate />}
      <div
        style={{
          width: "min(460px, 86vw)",
          maxHeight: "84vh",
          overflow: "auto",
          fontFamily: ChromeTokens.font.ui,
          color: ChromeTokens.color.ink,
          ...sectionGap,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 20 }}>Subscription</h2>

        {/* Dunning / paused / resolved banner — top of the modal. */}
        {(showDunning || showResolved) && (
          <DunningBanner
            stage={showResolved ? null : dunningStage}
            dueDate={view.dunningDue}
            resolved={showResolved}
            onUpdateCard={() => setAddingCard(true)}
            onResume={() => void run(() => b.reactivate())}
            onDismiss={() => setDunningDismissed(true)}
          />
        )}

        {/* Seat choice pending — first thing the grown-up sees (seat unit). */}
        {choicePending && seatsState && pendingIds && (
          <div
            data-testid="seat-choice-pending"
            style={{
              borderRadius: ChromeTokens.radius.lg,
              border: `2px solid ${ChromeTokens.color.sun}`,
              padding: ChromeTokens.spacing.lg,
            }}
          >
            <strong style={{ fontSize: 16 }}>Your plan changed — choose who keeps creating</strong>
            <KeptKidPicker
              members={members ?? []}
              keepCount={seatsState.count}
              value={pendingIds}
              onChange={setPendingIds}
              targetPlanName={currentPlan.name}
            />
            <button
              type="button"
              disabled={pending || pendingIds.length !== seatsState.count}
              onClick={() => void run(() => b.setSeats(pendingIds))}
              style={{
                marginTop: ChromeTokens.spacing.md,
                width: "100%",
                minHeight: 48,
                border: "none",
                borderRadius: ChromeTokens.radius.pill,
                background: ChromeTokens.color.sun,
                color: ChromeTokens.color.ink,
                fontFamily: ChromeTokens.font.ui,
                fontSize: 15,
                fontWeight: 800,
                cursor: pending ? "not-allowed" : "pointer",
                opacity: pendingIds.length !== seatsState.count ? 0.55 : 1,
              }}
            >
              Save who keeps creating
            </button>
          </div>
        )}

        {/* current plan card */}
        <div
          style={{
            borderRadius: ChromeTokens.radius.lg,
            border: `1.5px solid ${ChromeTokens.color.baseMuted}`,
            padding: ChromeTokens.spacing.lg,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: ChromeTokens.spacing.sm + 2 }}>
            <strong style={{ fontSize: 18 }}>{currentPlan.name}</strong>
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: ChromeTokens.color.ink,
                background: badge.tokenColor,
                padding: "3px 10px",
                borderRadius: ChromeTokens.radius.pill,
              }}
            >
              {badge.label}
            </span>
            <span style={{ marginLeft: "auto", fontSize: 18, fontWeight: 900 }}>
              {money(currentPlan.price)}
              <span style={{ fontSize: 12, fontWeight: 700, color: ChromeTokens.color.inkMuted }}>/mo</span>
            </span>
          </div>
          <div style={{ fontSize: 13, color: ChromeTokens.color.inkMuted, marginTop: ChromeTokens.spacing.xs + 2, display: "flex", alignItems: "center", gap: ChromeTokens.spacing.xs + 2 }}>
            <Icon name="calendar-clock" size={14} stroke={ChromeTokens.color.inkMuted} /> {statusLine()}
          </div>
          {/* Who holds the plan's seats (seat unit): names once the roster is in. */}
          {seatsState && !view.locked && (
            <div
              data-testid="plan-seat-holders"
              style={{ fontSize: 13, color: ChromeTokens.color.inkMuted, marginTop: ChromeTokens.spacing.xs + 2, display: "flex", alignItems: "center", gap: ChromeTokens.spacing.xs + 2 }}
            >
              <Icon name="wand-sparkles" size={14} stroke={ChromeTokens.color.inkMuted} />
              <span>
                {seatHolderLine(seatsState.holderIds, seatsState.count, members)}
              </span>
            </div>
          )}
        </div>

        {/* Store- or elsewhere-managed notice — hide all in-app controls (10.5). */}
        {rail !== "here" && (
          <div
            data-testid={`manage-rail-${rail}`}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: ChromeTokens.spacing.md,
              padding: `13px 14px`,
              borderRadius: ChromeTokens.radius.md,
              background: `color-mix(in srgb, var(--chrome-color-sky) 22%, var(--chrome-color-base))`,
            }}
          >
            <Icon name="shield" size={18} stroke={ChromeTokens.color.clay} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              {rail === "store_here" && (
                <>
                  <strong>Billed through {plat.store}.</strong> Because you subscribed on this device, your plan,
                  payment, and cancellation are managed there: <em>{plat.path}</em>.
                  {plat.id === "ios" && appStoreAvailable() && (
                    <div>
                      <button
                        type="button"
                        onClick={() =>
                          gate.run("purchase", () => {
                            setStoreError(false);
                            void manageOnAppStore().then((ok) => setStoreError(!ok));
                          })
                        }
                        style={{ marginTop: ChromeTokens.spacing.sm, minHeight: 44, padding: "0 16px", borderRadius: ChromeTokens.radius.md, border: `1px solid ${ChromeTokens.color.inkMuted}`, background: "none", color: ChromeTokens.color.ink, fontFamily: ChromeTokens.font.ui, fontWeight: 800, cursor: "pointer" }}
                      >
                        Manage in the App Store
                      </button>
                      {storeError && (
                        <div role="status" style={{ marginTop: ChromeTokens.spacing.xs, fontSize: 13, color: ChromeTokens.color.inkMuted }}>
                          We couldn't open the App Store. You can manage it in {plat.path}.
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
              {rail === "app_store_elsewhere" && (
                <>
                  <strong>Billed through the App Store.</strong> This plan was bought in the iPad app, so its
                  payment and cancellation are managed on that iPad: <em>Settings ▸ your name ▸ Subscriptions</em>.
                </>
              )}
              {rail === "web_elsewhere" && (
                <>
                  <strong>Your plan is already active here.</strong> It was set up on the world by me website, so
                  its payment and cancellation are managed there.
                </>
              )}
            </div>
          </div>
        )}

        {/* Web-only controls, for a plan the web owns. */}
        {rail === "here" && (
          <>
            {/* payment method */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: ChromeTokens.spacing.md,
                padding: `12px 14px`,
                borderRadius: ChromeTokens.radius.md,
                background: ChromeTokens.color.baseMuted,
              }}
            >
              <Icon name="credit-card" size={18} stroke={ChromeTokens.color.ink} />
              <div style={{ flex: 1, fontSize: 13.5 }}>
                {view.card ? (
                  <>
                    Card ending in <strong>{view.card.last4 || "••••"}</strong>
                  </>
                ) : (
                  <>No card on file</>
                )}
              </div>
              <button
                type="button"
                onClick={() => setAddingCard((v) => !v)}
                style={{ background: "none", border: "none", cursor: "pointer", color: ChromeTokens.color.clay, fontWeight: 700, fontSize: 13, textDecoration: "underline", minHeight: 44 }}
              >
                {view.card ? "Update" : "Add"}
              </button>
            </div>

            {addingCard && (
              <div
                style={{
                  padding: ChromeTokens.spacing.lg,
                  borderRadius: ChromeTokens.radius.md,
                  border: `1.5px solid ${ChromeTokens.color.baseMuted}`,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 800, marginBottom: ChromeTokens.spacing.md }}>
                  {inDunning || dunningStage === "paused"
                    ? "Update your card to keep things running"
                    : view.trialNoCard ? "Add a card to keep creating after your trial" : "Update payment method"}
                </div>
                <CheckoutPanel
                  planId={view.planId}
                  mode="addcard"
                  showPlanPicker={view.trialNoCard}
                  ctaLabel="Save card"
                  onDone={() => {
                    setAddingCard(false);
                    // After a successful card update from any dunning state,
                    // flip to the resolved success line. The next refresh
                    // from the webhook will clear ``dunning_stage`` on the
                    // server; the banner stays in resolved-success until
                    // the parent dismisses the modal.
                    if (inDunning || dunningStage === "paused") {
                      setDunningResolved(true);
                    }
                  }}
                />
              </div>
            )}

            {/* change plan */}
            {view.status !== "locked" && (
              <div style={sectionGap}>
                <div style={{ fontSize: 13, fontWeight: 800 }}>Change plan</div>
                <div style={{ display: "flex", flexDirection: "column", gap: ChromeTokens.spacing.sm }}>
                  {WBM_PLANS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPlanTarget(p.id)}
                      disabled={pending}
                      aria-pressed={planTarget === p.id}
                      style={{
                        textAlign: "left",
                        cursor: pending ? "not-allowed" : "pointer",
                        padding: `10px 14px`,
                        borderRadius: ChromeTokens.radius.md,
                        background: ChromeTokens.color.base,
                        color: ChromeTokens.color.ink,
                        fontFamily: ChromeTokens.font.ui,
                        fontSize: 13.5,
                        minHeight: 44,
                        border:
                          planTarget === p.id
                            ? `2px solid ${ChromeTokens.color.sun}`
                            : `1.5px solid ${ChromeTokens.color.baseMuted}`,
                      }}
                    >
                      <strong>{p.name}</strong> — {money(p.price)}/mo, up to {p.seats} kid{p.seats === 1 ? "" : "s"}
                    </button>
                  ))}
                </div>
                {downgradeLosesSeats && planTarget && (
                  <div
                    style={{
                      padding: `11px 12px`,
                      borderRadius: ChromeTokens.radius.md,
                      background: `color-mix(in srgb, var(--chrome-color-coral) 16%, var(--chrome-color-base))`,
                    }}
                  >
                    {/* the existing one-line "what happens" warning — kept verbatim */}
                    <div style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
                      <Icon name="alert-triangle" size={16} stroke={ChromeTokens.color.clay} style={{ flexShrink: 0, marginTop: 1 }} />
                      <div style={{ fontSize: 12.5, lineHeight: 1.45 }}>
                        <strong>{planById(planTarget).name} covers {planById(planTarget).seats} {planById(planTarget).seats === 1 ? "creator" : "creators"}.</strong>{" "}
                        {familySize} people in your family create on this plan — whoever you don't pick moves to
                        Explore mode (play &amp; explore, no creating) until you upgrade again. Nothing is deleted,
                        and anyone on their own plan isn't affected.
                      </div>
                    </div>
                    {/* the new picker (lazy roster — falls back to bare warning if /family/profiles is unavailable) */}
                    {members && members.length > 0 && (
                      <KeptKidPicker
                        members={members}
                        keepCount={keepCount}
                        value={keptIds}
                        onChange={setKeptIds}
                        targetPlanName={planById(planTarget).name}
                      />
                    )}
                  </div>
                )}
                {planTarget && (
                  <div style={{ display: "flex", gap: ChromeTokens.spacing.sm, alignItems: "center" }}>
                    <button
                      type="button"
                      onClick={handleChangePlan}
                      disabled={pending || !keptValid}
                      style={{
                        minHeight: 44,
                        padding: `8px 16px`,
                        borderRadius: ChromeTokens.radius.pill,
                        border: "none",
                        cursor: pending || !keptValid ? "not-allowed" : "pointer",
                        background: ChromeTokens.color.sun,
                        color: ChromeTokens.color.ink,
                        fontFamily: ChromeTokens.font.ui,
                        fontWeight: 800,
                        fontSize: 14,
                        boxShadow: ChromeTokens.shadow.soft,
                        opacity: !keptValid ? 0.55 : 1,
                      }}
                    >
                      Switch to {planById(planTarget).name}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPlanTarget(null)}
                      style={{ background: "none", border: "none", cursor: "pointer", color: ChromeTokens.color.inkMuted, fontWeight: 700, fontSize: 13, minHeight: 44 }}
                    >
                      Cancel
                    </button>
                    {downgradeLosesSeats && !keptValid && (
                      <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: ChromeTokens.color.clay }}>
                        Pick {keepCount} to continue
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* primary action by status */}
            <div style={{ borderTop: `1px solid ${ChromeTokens.color.baseMuted}`, paddingTop: ChromeTokens.spacing.lg, ...sectionGap }}>
              {(view.status === "active" || view.status === "trialing") &&
                (confirmCancel ? (
                  <div
                    style={{
                      background: `color-mix(in srgb, var(--chrome-color-coral) 16%, var(--chrome-color-base))`,
                      borderRadius: ChromeTokens.radius.md,
                      padding: ChromeTokens.spacing.lg,
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: ChromeTokens.spacing.xs }}>Cancel your subscription?</div>
                    <p style={{ fontSize: 13, color: ChromeTokens.color.inkMuted, margin: `0 0 ${ChromeTokens.spacing.md}px`, lineHeight: 1.45 }}>
                      You'll keep creating until the period ends. After that it switches to Explore mode — your worlds
                      stay safe and you can resume anytime.
                    </p>
                    <div style={{ display: "flex", gap: ChromeTokens.spacing.sm, alignItems: "center" }}>
                      <button
                        type="button"
                        onClick={() => void run(async () => { await b.cancel(); setConfirmCancel(false); })}
                        disabled={pending}
                        style={{
                          minHeight: 44,
                          padding: `8px 16px`,
                          borderRadius: ChromeTokens.radius.pill,
                          border: "none",
                          cursor: pending ? "not-allowed" : "pointer",
                          background: ChromeTokens.color.danger,
                          color: ChromeTokens.color.base,
                          fontFamily: ChromeTokens.font.ui,
                          fontWeight: 800,
                          fontSize: 13,
                        }}
                      >
                        Yes, cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmCancel(false)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: ChromeTokens.color.inkMuted, fontWeight: 700, fontSize: 13, minHeight: 44 }}
                      >
                        Keep my plan
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmCancel(true)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: ChromeTokens.color.danger, fontWeight: 700, fontSize: 13.5, textDecoration: "underline", minHeight: 44, alignSelf: "flex-start" }}
                  >
                    Cancel subscription
                  </button>
                ))}

              {view.status === "canceled" && (
                <button
                  type="button"
                  onClick={() => void run(() => b.reactivate())}
                  disabled={pending}
                  style={{
                    width: "100%",
                    minHeight: 48,
                    borderRadius: ChromeTokens.radius.pill,
                    border: "none",
                    cursor: pending ? "not-allowed" : "pointer",
                    background: ChromeTokens.color.sun,
                    color: ChromeTokens.color.ink,
                    fontFamily: ChromeTokens.font.ui,
                    fontWeight: 800,
                    fontSize: 15,
                    boxShadow: ChromeTokens.shadow.soft,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: ChromeTokens.spacing.sm,
                  }}
                >
                  <Icon name="rotate-ccw" size={15} stroke={ChromeTokens.color.ink} /> Resume my plan
                </button>
              )}

              {view.status === "locked" && onUnlock && (
                <button
                  type="button"
                  onClick={() => { onClose(); onUnlock("locked"); }}
                  style={{
                    width: "100%",
                    minHeight: 48,
                    borderRadius: ChromeTokens.radius.pill,
                    border: "none",
                    cursor: "pointer",
                    background: ChromeTokens.color.sun,
                    color: ChromeTokens.color.ink,
                    fontFamily: ChromeTokens.font.ui,
                    fontWeight: 800,
                    fontSize: 15,
                    boxShadow: ChromeTokens.shadow.soft,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: ChromeTokens.spacing.sm,
                  }}
                >
                  <Icon name="sparkles" size={15} stroke={ChromeTokens.color.ink} /> Subscribe again
                </button>
              )}
            </div>
          </>
        )}

        {error && <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: ChromeTokens.color.danger }}>{error}</p>}

        <DeleteAccountEntry family={family} context="manage" />
      </div>
    </PanelSurface>
  );
}
